import { lazy } from "react";
import { LocalizedNavigate } from "@/router/localized-navigate";
import {
	ACCOUNT_SETTINGS_ROUTE,
	API_KEYS_ROUTE,
	CREATE_EVENT_RULE_ROUTE,
	CUSTOM_FIELDS_ROUTE,
	CUSTOMERS_DETAILS,
	CUSTOMERS_ROUTE,
	DASHBOARD_ROUTE,
	DEVICES_DETAILS,
	DEVICES_ROUTE,
	EVENT_DETAILS,
	EVENTS_ROUTE,
	LEGACY_ACCOUNT_SETTINGS_ROUTE,
	LEGACY_API_KEYS_ROUTE,
	LEGACY_TEAM_MANAGEMENT_ROUTE,
	LEGACY_WEBHOOKS_ROUTE,
	LISTS_DETAILS_ROUTE,
	LISTS_ROUTE,
	LOGS_AUDIT_TRAIL_ROUTE,
	LOGS_EVENT_REPORTS_ROUTE,
	LOGS_ROUTE,
	NOT_FOUND_ROUTE,
	RULES_EVENTS_ROUTE,
	RULES_MODEL_ROUTE,
	RULES_ROUTE,
	RULES_THRESHOLD_ROUTE,
	SESSION_DETAILS,
	SESSIONS_ROUTE,
	TEAM_MANAGEMENT_ROUTE,
	WEBHOOKS_ROUTE,
} from "../../constants/routes";
import type { RouteConfig, RouteGroup } from "./main.types";

// Lazy load components
const Events = lazy(() => import("../Events"));
const EventDetails = lazy(() => import("../Events/event-details"));
const Dashboard = lazy(() => import("../Dashboard"));
const Customers = lazy(() => import("../Customers"));
const CustomerDetails = lazy(() => import("../Customers/customer-details"));
const Devices = lazy(() => import("../Devices/devices"));
const DeviceDetails = lazy(() => import("../Devices/device-details"));
const ListDetails = lazy(() => import("../Configuration/Lists/list-details"));
const Lists = lazy(() => import("../Configuration/Lists"));
const Rules = lazy(() => import("../Configuration/Rules/Rules"));
const AccountSettings = lazy(() => import("../AccountSettings"));
const TeamManagement = lazy(() => import("../TeamManagement"));
const APIKeys = lazy(() => import("../ApiKeys"));
const Webhooks = lazy(() => import("../Webhooks"));
const Threshold = lazy(() => import("../Configuration/Rules/Threshold"));
const ModelConfig = lazy(
	() => import("../Configuration/Rules/ModelConfiguration"),
);
const CreateEventRule = lazy(
	() => import("../Configuration/Rules/CreateEventRule"),
);
const CustomFields = lazy(() => import("../Configuration/CustomFields"));
const EventReports = lazy(() => import("../Logs/EventReports"));
const AuditTrail = lazy(() => import("../Logs/AuditTrail"));
const Sessions = lazy(() => import("../Sessions"));
const SessionDetails = lazy(() => import("../Sessions/session-details"));
const NotFoundPage = lazy(() => import("../NotFoundPage"));

// Common routes available to all users
export const commonRoutes: RouteConfig[] = [
	{
		path: ACCOUNT_SETTINGS_ROUTE,
		element: <AccountSettings />,
	},
];

// Redirects from pre-consolidation settings paths to their /settings/* equivalents.
export const legacySettingsRedirectRoutes: RouteConfig[] = [
	{
		path: LEGACY_ACCOUNT_SETTINGS_ROUTE,
		element: <LocalizedNavigate to={ACCOUNT_SETTINGS_ROUTE} replace />,
	},
	{
		path: LEGACY_TEAM_MANAGEMENT_ROUTE,
		element: <LocalizedNavigate to={TEAM_MANAGEMENT_ROUTE} replace />,
	},
	{
		path: LEGACY_API_KEYS_ROUTE,
		element: <LocalizedNavigate to={API_KEYS_ROUTE} replace />,
	},
	{
		path: LEGACY_WEBHOOKS_ROUTE,
		element: <LocalizedNavigate to={WEBHOOKS_ROUTE} replace />,
		hiddenSelfHosted: true,
	},
];

// Device fingerprint only subscription routes
export const deviceFingerprintOnlyRoutes: RouteConfig[] = [
	...commonRoutes,
	{
		path: API_KEYS_ROUTE,
		element: <APIKeys />,
	},
	{
		path: NOT_FOUND_ROUTE,
		element: <NotFoundPage />,
	},
];

// Admin routes for device fingerprint only subscription
export const deviceFingerprintAdminRoutes: RouteConfig[] = [
	{
		path: TEAM_MANAGEMENT_ROUTE,
		element: <TeamManagement />,
		requiresAdmin: true,
	},
	{
		path: LOGS_AUDIT_TRAIL_ROUTE,
		element: <AuditTrail />,
		requiresAdmin: true,
	},
	{
		path: LOGS_ROUTE,
		element: <LocalizedNavigate to={LOGS_AUDIT_TRAIL_ROUTE} replace />,
	},
];

// Full subscription routes (grouped by feature area)
export const fullSubscriptionRouteGroups: RouteGroup[] = [
	{
		name: "core",
		routes: [
			{
				path: DASHBOARD_ROUTE,
				element: <Dashboard />,
				hiddenSelfHosted: true,
			},
			...commonRoutes,
		],
	},
	{
		name: "events",
		routes: [
			{
				path: EVENTS_ROUTE,
				element: <Events />,
			},
			{
				path: EVENT_DETAILS(),
				element: <EventDetails />,
			},
		],
	},
	{
		name: "sessions",
		routes: [
			{
				path: SESSIONS_ROUTE,
				element: <Sessions />,
				requiredFlag: "behavioral-biometrics-pages",
			},
			{
				path: SESSION_DETAILS(),
				element: <SessionDetails />,
				requiredFlag: "behavioral-biometrics-pages",
			},
		],
	},
	{
		name: "customers",
		routes: [
			{
				path: CUSTOMERS_ROUTE,
				element: <Customers />,
			},
			{
				path: CUSTOMERS_DETAILS(),
				element: <CustomerDetails />,
			},
		],
	},
	{
		name: "devices",
		routes: [
			{
				path: DEVICES_ROUTE,
				element: <Devices />,
			},
			{
				path: DEVICES_DETAILS(),
				element: <DeviceDetails />,
			},
		],
	},
	{
		name: "configuration",
		routes: [
			{
				path: LISTS_ROUTE,
				element: <Lists />,
			},
			{
				path: LISTS_DETAILS_ROUTE(),
				element: <ListDetails />,
			},
		],
	},
	{
		name: "logs",
		routes: [
			{
				path: LOGS_ROUTE,
				element: <LocalizedNavigate to={LOGS_EVENT_REPORTS_ROUTE} replace />,
			},
			{
				path: LOGS_EVENT_REPORTS_ROUTE,
				element: <EventReports />,
			},
		],
	},
	{
		name: "error",
		routes: [
			{
				path: NOT_FOUND_ROUTE,
				element: <NotFoundPage />,
			},
		],
	},
];

// Admin-only routes for full subscription
export const fullSubscriptionAdminRoutes: RouteConfig[] = [
	{
		path: TEAM_MANAGEMENT_ROUTE,
		element: <TeamManagement />,
		requiresAdmin: true,
	},
	{
		path: CUSTOM_FIELDS_ROUTE,
		element: <CustomFields />,
		requiresAdmin: true,
	},
	{
		path: RULES_ROUTE,
		element: <LocalizedNavigate to={RULES_EVENTS_ROUTE} replace />,
		requiresAdmin: true,
	},
	{
		path: RULES_EVENTS_ROUTE,
		element: <Rules />,
		requiresAdmin: true,
	},
	{
		path: RULES_THRESHOLD_ROUTE,
		element: <Threshold />,
		requiresAdmin: true,
	},
	{
		path: RULES_MODEL_ROUTE,
		element: <ModelConfig />,
		requiresAdmin: true,
	},
	{
		path: CREATE_EVENT_RULE_ROUTE,
		element: <CreateEventRule />,
		requiresAdmin: true,
		requiredFlag: "ai-rule-builder",
		hiddenSelfHosted: true,
	},
	{
		path: API_KEYS_ROUTE,
		element: <APIKeys />,
		requiresAdmin: true,
	},
	{
		path: WEBHOOKS_ROUTE,
		element: <Webhooks />,
		requiresAdmin: true,
		hiddenSelfHosted: true,
	},
	{
		path: LOGS_AUDIT_TRAIL_ROUTE,
		element: <AuditTrail />,
		requiresAdmin: true,
	},
];
